import { Button, Card } from '@zenid/web';
import { useCallback, useMemo, useState } from 'react';
import { css } from '../../../../styled-system/css';
import { flex } from '../../../../styled-system/patterns';
import { INTEGRATION_IRREVERSIBLE_MESSAGE, IntegrationConsent } from './IntegrationConsent';

// registeredEmail: 入力されたメールアドレスが既存アカウントだったため誘導するケース
// ownedAccount: 「個人のZEN IDを既にお持ちの方はこちら」から自発的に遷移するケース
export type IntegrationSignInGuideOrigin = { type: 'registeredEmail'; email: string } | { type: 'ownedAccount' };

const TITLES: Record<IntegrationSignInGuideOrigin['type'], string> = {
  registeredEmail: 'このメールアドレスは登録済みです',
  ownedAccount: 'お持ちのアカウントと統合します',
};

type Props = {
  origin: IntegrationSignInGuideOrigin;
  proceed: () => Promise<void>;
};

export function IntegrationSignInGuide({ origin, proceed }: Props) {
  const [consented, setConsented] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const disabled = useMemo(() => !consented || submitting, [consented, submitting]);

  const handleSubmit = useCallback(async () => {
    if (disabled) {
      return;
    }
    setSubmitting(true);
    await proceed();
    // リダイレクトされるのが想定挙動のため submitting を false に戻さない
  }, [disabled, proceed]);

  return (
    <Card
      className={css({
        width: '343px',
        backgroundColor: '{colors.background.white}',
        padding: '{spacing.40} {spacing.20}',
        sm: { width: '400px', padding: '{spacing.40}' },
      })}
    >
      <div
        className={flex({
          flexDirection: 'column',
          gap: '{spacing.16}',
        })}
      >
        <h1
          className={css({
            textStyle: 'headding.l',
            textAlign: 'center',
            color: '{colors.text.main}',
            wordBreak: 'keep-all',
          })}
        >
          {TITLES[origin.type]}
        </h1>
        <p className={css({ textStyle: 'body.m.default', color: '{colors.text.sub}' })}>
          {origin.type === 'registeredEmail' && (
            <>
              <span className={css({ wordBreak: 'break-all' })}>{origin.email}</span>
              はすでにZEN IDに登録されています。次の画面でこのメールアドレスのZEN
              IDでログインすると、現在ご利用中のアカウントと統合されます。
            </>
          )}
          {origin.type === 'ownedAccount' && (
            <>
              次の画面で、すでにお持ちの個人のZEN
              IDでログインしてください。ログインが完了すると、現在ご利用中のアカウントとお持ちのアカウントが統合されます。
            </>
          )}
        </p>
        <IntegrationConsent message={INTEGRATION_IRREVERSIBLE_MESSAGE} checked={consented} onChange={setConsented} />
      </div>
      <Button
        color="primary"
        size="l"
        stretch="container"
        disabled={disabled}
        onClick={handleSubmit}
        className={css({ marginTop: '{spacing.24}' })}
      >
        ログインへ進む
      </Button>
    </Card>
  );
}
